import { Component, Input } from '@angular/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import { PdfViewerModalComponent } from '../pdf-viewer-modal/pdf-viewer-modal.component';
import { inlineImagesAsDataUrls, renderElementToCanvas, isIOS } from 'src/app/shared/functions/pdf-report.util';
import {
  ActiveRoutesDetail,
  ProductDetailField,
} from '../active-routes-detail/interfaces/ActiveRoutesDetail.interface';
import { GetValuesService } from '../../../../core/services/get-values.service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { LoadingDescriptionKeys } from 'src/app/core/enums/Loading-descriptions';
import { AlertService } from 'src/app/core/services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import {
  PdfReportError,
  PdfGenerationError,
  PDF_REPORT_ERROR_I18N_KEY,
  logPdfError,
  logPdfWarning,
} from 'src/app/shared/functions/pdf-report-errors';

@Component({
  selector: 'app-report-modal',
  templateUrl: './report-modal.component.html',
  styleUrls: ['./report-modal.component.scss'],
})
export class ReportModalComponent {
  @Input() detailsData!: ActiveRoutesDetail;
  @Input() orderHash!: string;
  @Input() isEconomicReport: boolean = false;
  @Input() autoDownload: boolean = false;
  @Input() brandingLogo?: string;
  @Input() hideContent: boolean = false;
  @Input() viewMode: boolean = false;

  paginatedPickupImages: string[][] = []
  paginatedDropoffImages: string[][] = []

  showPDF: boolean = false;
  private isGeneratingPdf: boolean = false;
  private readonly PDF_PAGE_TIMEOUT_MS = 20000;

  get logoUrl(): string {
    if (this.brandingLogo && this.brandingLogo.trim() !== '') {
      return this.brandingLogo;
    }
    return '/assets/caffer-logo-full.svg';
  }

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private loader: LoaderService,
    private getValuesService: GetValuesService,
    private alertService: AlertService,
    private translateService: TranslateService
  ) { }

  async ngOnInit() {
    this.paginatedPickupImages = this.getPaginatedPickupImages()
    this.paginatedDropoffImages = this.getPaginatedDropoffImages()

    if (this.autoDownload) {
      setTimeout(() => {
        this.downloadPdf(true);
      }, 100);
    } else if (this.viewMode) {
      this.hideContent = true;
      setTimeout(() => {
        this.generateAndView();
      }, 100);
    }
  }

  closeModal() {
    this.bsModalRef.hide()
  }


  getPaginatedPickupImages(): string[][] {
    const images = this.detailsData.TrackingInformation?.PickupImages || [];
    const pageSize = 4;
    const pages: string[][] = [];

    for (let i = 0; i < images.length; i += pageSize) {
      pages.push(images.slice(i, i + pageSize));
    }

    return pages;
  }

  getPaginatedDropoffImages(): string[][] {
    const images = this.detailsData.TrackingInformation?.DropoffImages || [];
    const pageSize = 4;
    const pages: string[][] = [];

    for (let i = 0; i < images.length; i += pageSize) {
      pages.push(images.slice(i, i + pageSize));
    }

    return pages;
  }
  async generateAndView() {
    const modalContent = document.getElementById('pdf-content');

    const excludedElement = document.getElementById('download-button');
    if (excludedElement) excludedElement.style.display = 'none';

    this.showPDF = true;
    await new Promise<void>(resolve => setTimeout(resolve, 300));

    if (!modalContent) { this.loader.hide(); return; }
    const clone = modalContent.cloneNode(true) as HTMLElement;
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;top:-9999px;left:0;width:794px;background:#fff;';
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    await new Promise<void>(resolve => setTimeout(resolve, 150));
    await inlineImagesAsDataUrls(clone);

    const pages = clone.getElementsByClassName('page');
    if (pages.length === 0) {
      document.body.removeChild(wrapper);
      this.loader.hide();
      this.closeModal();
      return;
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 208;

    const renderPage = async (index: number) => {
      if (index >= pages.length) {
        const blob = pdf.output('blob');
        const pdfName = `${this.orderHash}_${this.isEconomicReport ? 'economic' : 'service'
          }_report.pdf`;
        const initialState = { pdfName, pdfBlob: blob, showTerms: !this.isEconomicReport };
        const modalOptions: ModalOptions = {
          initialState,
          class: 'modal-dialog-centered modal-pdf-viewer',
        };
        this.modalService.show(PdfViewerModalComponent, modalOptions);
        document.body.removeChild(wrapper);
        if (excludedElement) excludedElement.style.display = 'block';
        this.showPDF = false;
        this.loader.hide();
        this.closeModal();
        return;
      }

      const page = pages[index] as HTMLElement;
      page.style.display = 'block';

      try {
        const canvas = await renderElementToCanvas(page, this.PDF_PAGE_TIMEOUT_MS);
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const margin = 10;
        const usableWidth = imgWidth - margin * 2;
        const imgHeight = (canvas.height * usableWidth) / canvas.width;
        if (index > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', margin, margin, usableWidth, imgHeight);
      } catch (error) {
        console.error('Error rendering page:', error);
      } finally {
        page.style.display = '';
        renderPage(index + 1);
      }
    };

    renderPage(0);
  }

  async downloadPdf(closeModal?: boolean) {
    if (this.isGeneratingPdf) {
      return;
    }

    this.isGeneratingPdf = true;
    closeModal ? this.loader.show(true, LoadingDescriptionKeys.LOADING_SERVICE) : this.loader.show();

    const excludedElement = document.getElementById('download-button');
    let wrapper: HTMLDivElement | null = null;

    try {
      if (excludedElement) {
        excludedElement.style.display = 'none';
      }

      this.showPDF = true;
      await this.delay(300);

      const modalContent = document.getElementById('pdf-content');
      if (!modalContent) {
        throw new PdfGenerationError(PdfReportError.CONTENT_NOT_FOUND);
      }

      const clone = modalContent.cloneNode(true) as HTMLElement;
      wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:absolute;top:-9999px;left:0;width:794px;background:#fff;';
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);
      await this.delay(150);
      await inlineImagesAsDataUrls(clone);

      const pages = clone.getElementsByClassName('page');
      if (pages.length === 0) {
        throw new PdfGenerationError(PdfReportError.NO_PAGES_GENERATED);
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 10;
      const usableWidth = 210 - margin * 2;
      let renderedPages = 0;

      for (let index = 0; index < pages.length; index++) {
        const page = pages[index] as HTMLElement;
        try {
          const canvas = await renderElementToCanvas(page, this.PDF_PAGE_TIMEOUT_MS);
          const imgData = canvas.toDataURL('image/jpeg', 0.92);
          const imgHeight = (canvas.height * usableWidth) / canvas.width;

          if (renderedPages > 0) {
            pdf.addPage();
          }
          pdf.addImage(imgData, 'JPEG', margin, margin, usableWidth, imgHeight);
          renderedPages++;
        } catch (error) {
          const code = error instanceof Error && error.message.includes('Timeout')
            ? PdfReportError.PAGE_RENDER_TIMEOUT
            : PdfReportError.PAGE_RENDER_FAILED;
          logPdfWarning(code, `page ${index + 1}`);
        }
      }

      if (renderedPages === 0) {
        throw new PdfGenerationError(PdfReportError.NO_PAGES_GENERATED);
      }

      let blob: Blob;
      try {
        blob = pdf.output('blob');
      } catch (error) {
        throw new PdfGenerationError(PdfReportError.EXPORT_FAILED);
      }

      const filename = `${this.orderHash}_${this.isEconomicReport ? 'economic' : 'service'}_report.pdf`;
      this.saveBlobWithFallback(blob, filename);

      if (closeModal) {
        this.closeModal();
      }
    } catch (err) {
      const code = err instanceof PdfGenerationError ? err.code : PdfReportError.UNKNOWN;
      this.showPdfError(code, err);
    } finally {
      if (wrapper && wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      }

      if (excludedElement) {
        excludedElement.style.display = 'block';
      }

      this.showPDF = false;
      this.isGeneratingPdf = false;
      this.loader.hide();
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private showPdfError(code: PdfReportError, detail?: unknown): void {
    logPdfError(code, detail);
    const text = this.translateService.instant(PDF_REPORT_ERROR_I18N_KEY, { code });
    this.alertService.throwErrorMessage(text);
  }

  private saveBlobWithFallback(blob: Blob, filename: string): void {
    if (!isIOS()) {
      saveAs(blob, filename);
      return;
    }

    // iOS Safari renders blob: PDFs unreliably (blank or blank first page). Show
    // the in-app PDF.js viewer instead, which renders consistently and lets the
    // user download from there.
    this.modalService.show(PdfViewerModalComponent, {
      initialState: { pdfName: filename, pdfBlob: blob, showTerms: false },
      class: 'modal-dialog-centered modal-pdf-viewer',
    });
  }


  getFuelType(id?: number): string {
    if (!id) return 'Desconocido';
    return this.getValuesService.getFuelTypeString(id);
  }

  getChassisType(id?: number): string {
    if (!id) return 'Desconocido';
    return this.getValuesService.getChassisTypeString(id);
  }

  getServiceType(id?: number): string {
    if (!id) return 'Desconocido';
    return this.getValuesService.getServiceTypeString(id);
  }

  getOrderStatus(id?: number): string {
    if (!id) return 'Desconocido';
    return this.getValuesService.getOrderStatusString(id);
  }

  getVehicleIcon(id: number | undefined): string {
    if (!id) { return '' }
    return this.getValuesService.getChassisTypeIcon(id);
  }
}
